import React, { useEffect, FC, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import cn from 'classnames';
import { Form, Formik } from 'formik';
import Button, { ButtonColor, ButtonSize } from 'src/shared-components/Button';
import usePostNewReview from 'src/api/hooks/usePostNewReview';
import NextImage from 'src/shared-components/NextImage/NextImage';
import { useRouter } from 'next/router';
import { DrugReview, TradenameItem } from 'src/api/types';
import getTradenamesReviews from 'src/api/requests/getTradenamesReviews';
import getDrugReviews from 'src/api/requests/getDrugReviews';
import ButtonSecondary from 'src/shared-components/design-system/ButtonSecondary';
import { generateDrugPageLink } from 'src/utils/links';
import useGTAG, { GTAGPrefix } from 'src/hooks/useGTAG';
import dayjs from 'dayjs';
import ButtonTertiary from '../design-system/ButtonTertiary';
import trns from './trns';
import Reviews from '../../page-components/drug/components/ReviewsComponents/Reviews';
import StarsComponent from '../../page-components/drug/components/ReviewsComponents/StarsComponent';
import ReviewNameField from '../../page-components/drug/components/ReviewsComponents/ReviewNameField';
import ReviewFormField from '../../page-components/drug/components/ReviewsComponents/ReviewFormField';
import StarsFormField from '../../page-components/drug/components/ReviewsComponents/StarsFormField';
import NavigationLink from '../NavigationLink/NavigationLink';

require('dayjs/locale/uk');

interface Props {
  drugTranslitname: string;
  drugName?: string;
  averageRate: number;
  rateCount: number;
  reviews: DrugReview[];
  isTradenamePage?: boolean;
  tradenameData?: TradenameItem;
  gtagPrefix?: GTAGPrefix;
}
const checkCircle = 'drug/check_circle.svg';
const reportProblem = 'drug/report_problem.svg';

const ReviewsComponent: FC<Props> = ({
  drugTranslitname,
  drugName = null,
  averageRate,
  rateCount,
  reviews,
  isTradenamePage,
  tradenameData,
  gtagPrefix
}) => {
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isResultRequestBlockOpen, setResultRequestBlockOpen] = useState(false);
  const intl = useIntl();
  const router = useRouter();
  const [reviewsListState, setReviewsList] = useState<DrugReview[]>([]);
  const [reviewsPage, setReviewsPage] = useState(1);

  useEffect(() => {
    if (reviews && reviews.length > 0) {
      setReviewsList(reviews);
    }
  }, [reviews?.length]);

  const { postNewReviewRequest, data: reviewUpdate } = usePostNewReview();
  const { trackGtagEvent } = useGTAG();

  useEffect(() => {
    if (router?.asPath.split('#')[1] === 'reviewsform') {
      setIsFormOpen(true);
    }
  }, [router.asPath]);

  useEffect(() => {
    if (isResultRequestBlockOpen) {
      trackGtagEvent(`${gtagPrefix}_review_form_done`);
    }
  }, [isResultRequestBlockOpen]);

  const validateFormik = (values) => {
    const errors = {
      name: null,
      review: null,
      stars: null
    };

    // name validation
    if (!values?.name) {
      errors.name = <FormattedMessage {...trns.requiredFieldLabel} />;
    } else if (values?.name?.length < 3) {
      errors.name = <FormattedMessage {...trns.minimumLength3} />;
    } else if (values?.name?.length > 30) {
      errors.name = <FormattedMessage {...trns.maxmumLength30} />;
    }

    // review validation
    if (!values?.review) {
      errors.review = <FormattedMessage {...trns.requiredFieldLabel} />;
    } else if (values?.review?.length < 5) {
      errors.review = <FormattedMessage {...trns.minimumLength5} />;
    }

    //  star validation
    if (!values?.stars) {
      errors.stars = <FormattedMessage {...trns.requiredFieldLabel} />;
    }

    if (!errors.name && !errors.review && !errors.stars) {
      return null;
    }
    return errors;
  };

  const handleFormSubmit = async (values) => {
    await postNewReviewRequest({
      drugTranslitName: drugTranslitname,
      userName: values.name,
      rating: values.stars,
      comment: values.review
    });
    setIsFormOpen(false);
    setResultRequestBlockOpen(true);
  };

  const getNewReviewsList = async (page) => {
    const requestSearchObject = isTradenamePage
      ? getTradenamesReviews({
          tradeNameTranslitName: tradenameData?.translitName,
          pageNumber: page,
          pageSize: 15
        })
      : getDrugReviews({
          drugTranslitName: drugTranslitname,
          pageNumber: page,
          pageSize: 15
        });
    const newReviews = await requestSearchObject;
    setReviewsList((state) => [...state, ...newReviews]);
  };

  const handleLoadMoreClick = () => {
    setReviewsPage((prev) => prev + 1);
    getNewReviewsList(reviewsPage);
  };

  useEffect(() => {
    setReviewsList([]);
    getNewReviewsList(0);
  }, [drugTranslitname, drugName]);

  const reviewExist = rateCount > 0;
  const reviewsList = reviewsListState.length > 0 ? reviewsListState : reviews;

  return (
    <div id="reviewsform">
      <div className="flex flex-col m:flex-row  m:justify-between mb-6">
        <p className="flex items-center mb-3 ">
          <span className="fs-header5-bold text-black">
            <FormattedMessage {...trns.productReviews} />{' '}
            {isTradenamePage && (
              <>
                <span>{tradenameData.name}</span>
                <p className="fs-body-reg text-black mt-2">
                  <FormattedMessage {...trns.toLeaveReview} />
                </p>
              </>
            )}
          </span>
        </p>
        <Reviews
          averageRate={averageRate}
          rateCount={rateCount}
          isLeaveReviewText={false}
        />
      </div>
      {reviewsList?.map((review, index) => (
        <div
          className="p-4 bg-wildSand rounded-lg border-solid border-2 border-alto mb-2.5"
          key={index}
        >
          {isTradenamePage && (
            <NavigationLink
              href={generateDrugPageLink({
                translitName: review.drugTranslitName
              })}
              onClick={() => {
                trackGtagEvent(
                  'tradename_reviews_item',
                  review.drugName,
                  'item_name'
                );
              }}
            >
              <p className="fs-body-lg-bold  text-bahamaBlue mb-2 underline decoration-1">
                {review.drugName}
              </p>
            </NavigationLink>
          )}
          <div className="flex mb-1">
            <p className="fs-body-lg-bold text-black mr-3">{review.userName}</p>
            <StarsComponent averageRate={review.rating} />
          </div>
          <p className="fs-caption-reg text-black mb-3">
            <FormattedMessage {...trns.fromDate} />{' '}
            <span>
              {dayjs(review.createDate).locale('uk').format('DD MMMM YYYY')}
            </span>
          </p>
          <p className="fs-body-reg">{review.comment}</p>
        </div>
      ))}
      {reviewExist && reviewsList?.length < rateCount && (
        <button
          className="w-full py-5 bg-wildSand rounded-lg fs-body-reg text-astronautBlue"
          onClick={handleLoadMoreClick}
        >
          <FormattedMessage {...trns.loadMoreReviews} />
        </button>
      )}
      {!reviewExist &&
        !isFormOpen &&
        !isResultRequestBlockOpen &&
        !isTradenamePage && (
          <div className=" flex flex-col align-center justify-center p-4 bg-wildSand rounded-lg ">
            <div className="flex justify-center ">
              <p className="w-[300px] text-center opacity-70">
                <FormattedMessage {...trns.noProductReviewsYet} />
              </p>
            </div>
            <div className="flex justify-center">
              <ButtonSecondary
                onClick={() => setIsFormOpen(true)}
                className="w-[220px] mt-4"
                label={
                  <p className="fs-body-bold">
                    {' '}
                    <FormattedMessage {...trns.LeaveReview} />
                  </p>
                }
              />
            </div>
          </div>
        )}
      {!isTradenamePage && (
        <button
          onClick={() => {
            setIsFormOpen(true);
            trackGtagEvent(`${gtagPrefix}_review_button`);
          }}
          className={cn({
            'w-full m:w-[220px] mt-4 py-[11px] px-[16px] bg-astronautBlue text-white fs-body-bold rounded-lg':
              true,
            hidden: isFormOpen || isResultRequestBlockOpen || !reviewExist
          })}
        >
          {' '}
          <FormattedMessage {...trns.LeaveReview} />
        </button>
      )}

      {isFormOpen && (
        <div className="w-full mt-8 p-6 border rounded-lg border-alto">
          <p className="fs-header5-bold text-black pl-1.5">
            <FormattedMessage {...trns.writeReview} />
          </p>
          <p className="fs-body-reg text-black pl-1.5">
            <FormattedMessage {...trns.yourReviewHelps} />
          </p>
          <Formik
            validateOnChange
            initialValues={{
              name: null,
              review: null,
              stars: 5
            }}
            onSubmit={(values) => handleFormSubmit(values)}
            validate={validateFormik}
          >
            {({ handleSubmit }) => (
              <Form>
                <div className="flex flex-col justify-center items-center w-full">
                  <div className="flex flex-col w-full pt-2 space-y-[24px]">
                    <StarsFormField
                      name={'stars'}
                      label={<FormattedMessage {...trns.rateProduct} />}
                      required
                    />
                    <ReviewNameField
                      name={'name'}
                      label={<FormattedMessage {...trns.yourName} />}
                      required
                    />
                    <ReviewFormField
                      name={'review'}
                      label={<FormattedMessage {...trns.writeReview} />}
                      placeholder={intl.formatMessage(trns.describeExperience)}
                      required
                    />
                  </div>
                  <div className="w-full flex justify-start mt-4">
                    {' '}
                    <p className="fs-caption-reg pl-1.5">
                      <FormattedMessage {...trns.importantly} />
                    </p>
                  </div>
                  <div className="w-full flex flex-col m:flex-row justify-center items-center px-[16px]">
                    <ButtonTertiary
                      onClick={() => {
                        setIsFormOpen(false);
                        trackGtagEvent(`${gtagPrefix}_review_form_cancel`);
                      }}
                      buttonLabel={<FormattedMessage {...trns.cancel} />}
                      className="mt-[25px] p-[0px] m:py-[10px] m:px-[16px] w-full m:w-[220px]"
                    />
                    <ButtonSecondary
                      onClick={() => {
                        handleSubmit();
                        trackGtagEvent(`${gtagPrefix}_review_form_send`);
                      }}
                      className="mt-[25px] py-[0px] m:py-[10px] px-[16px] w-full m:w-[220px]"
                      label={
                        <span className="uppercase">
                          <FormattedMessage {...trns.LeaveReview} />
                        </span>
                      }
                    />
                  </div>
                </div>
              </Form>
            )}
          </Formik>
        </div>
      )}
      {isResultRequestBlockOpen && reviewUpdate != null && (
        <div className=" w-full mt-8 p-6 border rounded-lg border-alto">
          {reviewUpdate.status === 200 ? (
            <>
              <div className="flex justify-center mb-4">
                <NextImage
                  alt="check Circle"
                  src={checkCircle}
                  layout="fixed"
                  width={48}
                  height={48}
                />
              </div>
              <p className="fs-body-lg-bold text-center mb-4">
                <FormattedMessage {...trns.feedbackSuccess} />
              </p>
              <p className=" fs-body-reg text-center   mb-4">
                <FormattedMessage {...trns.feedbackWillAppear} />
              </p>
              <div className="flex justify-center">
                <Button
                  onClick={() => setResultRequestBlockOpen(false)}
                  className="w-[220px]"
                  color={ButtonColor.blue}
                  size={ButtonSize.md}
                  type="submit"
                >
                  <FormattedMessage {...trns.ok} />
                </Button>
              </div>
            </>
          ) : (
            <>
              <div className="flex justify-center  mb-4">
                <NextImage
                  alt="report Problem"
                  src={reportProblem}
                  layout="fixed"
                  width={48}
                  height={48}
                />
              </div>
              <p className="fs-body-lg-bold text-center mb-4">
                <FormattedMessage {...trns.failedFeedback} />
              </p>
              <p className="fs-body-reg text-center mb-4">
                <FormattedMessage {...trns.tryAgainPlease} />
              </p>
              <div className="flex justify-center">
                <Button
                  onClick={() => setResultRequestBlockOpen(false)}
                  className="w-[220px]"
                  color={ButtonColor.blue}
                  size={ButtonSize.md}
                  type="submit"
                >
                  <FormattedMessage {...trns.tryAgain} />
                </Button>{' '}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default ReviewsComponent;
